/*
  Topture semantic enhancement for Easy Tabs product accordion headings.

  Easy Tabs currently renders every accordion row label as a non-heading
  control — `<div class="easytabs-accordion-item" role="button">` — so a
  buyer sees a major section title ("Specifications", "Shipping") that the
  document has no heading element for. This wraps each rendered row control
  in a real <h2> without changing the Shopify description <h5> split markers
  that the app uses to cut the description into rows.

  The app vendor does not currently expose native heading markup as a
  setting, and has said a storefront script is their only proposed route, so
  this upgrade exists in the rendered DOM only — Easy Tabs still emits the
  original non-heading markup in its server output.

  WHY WRAP RATHER THAN REPLACE
  ----------------------------
  easytabs.min.js binds click DIRECTLY to each .easytabs-accordion-item node
  during initWidget(), and never rebinds. Replacing the node with a fresh
  <h2><button> would silently drop that listener, so the node itself has to
  survive. Moving it into a wrapper preserves it.

  WHY THE WRAPPER IS SAFE (verified against easy-tabs-product-tabs-36)
  --------------------------------------------------------------------
  Everything the app does from the trigger walks UP with its own recursive
  easytabs.closestParent() (to .easytabs-holder / .easytabs-content-item) or
  DOWN from the content-item with querySelector(). Neither cares about an
  extra ancestor. It never uses trigger.nextElementSibling or
  trigger.parentElement — the one place a wrapper would have broken it. The
  sibling maths it does use (`t.previousElementSibling`, `:first-child`,
  `.easytabs-contents > div:first-child`, the `+` rule in its stylesheet) all
  operate on .easytabs-content-item level, which this does not touch.

  Likewise every app CSS selector reaching the trigger is a DESCENDANT
  combinator (`.easytabs-content-item .easytabs-accordion-item`), never a
  child combinator, so the wrapper is invisible to the cascade.

  See .planning/GOTCHAS.md — "Easy Tabs row labels are promoted to <h2> at
  runtime by wrapping, never replacing, the app's trigger node".
*/
(function () {
  'use strict';

  var MARK = 'data-tt-et-heading';
  var WRAP_CLASS = 'tt-et-heading';

  /* Wrap the top-level row triggers of one .easytabs-container. Only DIRECT
     children are considered at each step, so the hidden desktop tab strip
     (.easytabs-tabs > li.easytabs-tab-item, which carries the same labels)
     and any accordion nested inside panel content are both excluded by
     structure rather than by matching on label text. */
  function enhanceContainer(container) {
    var contents = container.querySelector('.easytabs-contents');
    if (!contents) return 0;

    var wrapped = 0;
    var items = contents.children;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item.classList || !item.classList.contains('easytabs-content-item')) continue;

      var trigger = null;
      for (var j = 0; j < item.children.length; j++) {
        var child = item.children[j];
        if (child.classList && child.classList.contains('easytabs-accordion-item')) {
          trigger = child;
          break;
        }
      }
      if (!trigger) continue;

      // Idempotency: already wrapped by an earlier pass, an app redraw or a
      // Theme Editor section reload. Never produces <h2><h2>.
      if (trigger.parentElement && trigger.parentElement.hasAttribute(MARK)) continue;

      var heading = document.createElement('h2');
      heading.className = WRAP_CLASS;
      heading.setAttribute(MARK, 'true');
      item.insertBefore(heading, trigger);
      heading.appendChild(trigger); // move, not clone — keeps the app's listener
      wrapped++;
    }

    return wrapped;
  }

  function enhance() {
    var containers = document.querySelectorAll('.easytabs-container');
    var total = 0;
    for (var i = 0; i < containers.length; i++) total += enhanceContainer(containers[i]);
    return total;
  }

  /* Narrowly scoped observer: childList on each .easytabs-contents only, so
     it fires when the app rebuilds rows and not for anything else on the
     page. Our own insertion re-enters once and finds nothing left to do,
     which terminates the cycle — no polling, no whole-document scan. */
  function observe() {
    if (typeof MutationObserver !== 'function') return;
    var contents = document.querySelectorAll('.easytabs-container .easytabs-contents');
    if (!contents.length) return;
    var observer = new MutationObserver(function () { enhance(); });
    for (var i = 0; i < contents.length; i++) {
      observer.observe(contents[i], { childList: true });
    }
  }

  function start() {
    enhance();
    observe();
  }

  // The app dispatches easytabs_page_inited on document after it wires every
  // widget up. We may run before or after that (both orders are safe — the
  // wrap is transparent to initWidget's own queries), so do a pass now and
  // another on the event, whichever lands first.
  document.addEventListener('easytabs_page_inited', enhance);
  // Theme Editor re-renders the app block's section.
  document.addEventListener('shopify:section:load', start);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
